import type { ReactElement } from 'react';
import { useCallback } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { cx } from '@/utils/cx';

import { InlineFieldError } from '../components/InlineFieldError';
import { OrgMemberPicker } from './OrgMemberPicker';
import { NOOP_SAVE, useInlineFieldEdit } from './useInlineFieldEdit';

interface TaskAssigneeFieldProps {
  ownerEntityId: string | null;
  orgId: number;
  onSave?: (targetIds: string[]) => Promise<void>;
  /**
   * True while the shared assignee mutation (see TaskAssigneeMutationContext)
   * is settling — either from this field's own save, or from the sibling
   * TaskActionFooter's Reassign button writing the same association. Disables
   * the trigger so a user can't open this picker while the other surface's
   * write to the same association is still in flight.
   */
  mutationPending?: boolean;
}

const READ_AFFORDANCE_CLASS =
  'inline-flex items-center rounded-md outline-brand hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2';
const EMPTY_VALUE_CLASS = 'text-xs text-fg-tertiary';

/**
 * Assignee row: the badge itself is the popover trigger for the searchable
 * `OrgMemberPicker` (search field + member list + Unassign) — the same picker
 * the footer's "Reassign" uses. The row's shape never changes (no inline
 * editor swap), so opening the picker can't reflow the overview; the trigger
 * stays mounted, so react-aria restores focus to it on close for free.
 *
 * Save lifecycle: a pick commits via `useInlineFieldEdit` — its `pendingRef`
 * serialises overlapping saves (a second pick while one is settling is
 * refused as `'ignored'`), and a rejection surfaces through `error` under the
 * row. The trigger is disabled while a save (or the sibling footer's write to
 * the same association) is in flight.
 */
export function TaskAssigneeField({
  ownerEntityId,
  orgId,
  onSave,
  mutationPending,
}: TaskAssigneeFieldProps): ReactElement {
  const { error, isSaving, commit } = useInlineFieldEdit(onSave ?? NOOP_SAVE);

  const editable = !!onSave;

  const handleSelect = useCallback(
    (entityId: string) => {
      // Re-selecting the current assignee is a no-op — don't issue a write.
      if (entityId === ownerEntityId) {
        return;
      }
      void commit([entityId]);
    },
    [ownerEntityId, commit]
  );

  const handleUnassign = useCallback(() => {
    void commit([]);
  }, [commit]);

  // Read-only display (no editor): the badge stays interactive so clicking it
  // opens the owner's slideout.
  const readValue = ownerEntityId ? (
    <EntityBadgeById entityId={ownerEntityId} orgId={orgId} avatar />
  ) : (
    <span className={EMPTY_VALUE_CLASS}>—</span>
  );

  if (!editable) {
    return readValue;
  }

  // Inside the trigger the badge must be non-interactive: an interactive
  // badge is a second focusable control, and pressing it opens the entity
  // slideout instead of the assignee picker.
  const triggerValue = ownerEntityId ? (
    <EntityBadgeById
      entityId={ownerEntityId}
      orgId={orgId}
      avatar
      interactive={false}
    />
  ) : (
    <span className={EMPTY_VALUE_CLASS}>—</span>
  );

  const disabled = mutationPending || isSaving;

  return (
    <div>
      <OrgMemberPicker
        orgId={orgId}
        selectedEntityId={ownerEntityId}
        onSelect={handleSelect}
        onUnassign={handleUnassign}
        aria-label='Change assignee'
      >
        <AriaButton
          aria-label='Edit assignee'
          isDisabled={disabled}
          className={cx(READ_AFFORDANCE_CLASS, disabled && 'opacity-50')}
        >
          {triggerValue}
        </AriaButton>
      </OrgMemberPicker>
      <InlineFieldError message={error} />
    </div>
  );
}
